<template>
  <div ref="containerRef" :class="[bem.b(), bem.is(size)]">
    <div
      ref="contentRef"
      :class="[
        bem.e('container'),
        bem.is(displayMode === 'light' ? 'light' : 'query'),
      ]"
      :style="{
        gap: gapSize,
        flexWrap: overflowDisplay === 'collapse' ? 'nowrap' : 'wrap',
      }"
    >
      <!-- 全选标签 -->
      <ll-tag
        v-if="showSelectAll && visibleSelectAll"
        ref="selectAllTagRef"
        :class="[bem.e('tag-item'), bem.is('all')]"
        :shape="shape"
        :color="allSelected ? activeColor : color"
        :bordered="bordered"
        :style="{
          ...tagItemStyle,
          ...(allSelected ? activeStyle : {}),
        }"
        @click="handleSelectAll"
      >
        {{ selectAllText }}
      </ll-tag>

      <!-- 可见标签 -->
      <template v-for="item in visibleTags" :key="item[valueKey]">
        <ll-tag
          :class="[bem.e('tag-item')]"
          :shape="shape"
          :color="selectedValues.includes(item[valueKey]) ? activeColor : color"
          :bordered="bordered"
          :icon-direction="iconDirection"
          :icon="{
            iconName: showIcon ? item[iconKey] || defaultIcon || '' : '',
            size: iconSize,
          }"
          :style="{
            ...tagItemStyle,
            ...(selectedValues.includes(item[valueKey]) ? activeStyle : {}),
          }"
          v-bind="
            displayMode === 'query'
              ? { onClick: (e: MouseEvent) => handleClick(e, item) }
              : {}
          "
        >
          <span :class="bem.m('text')">
            {{ item[labelKey] || item[valueKey] }}
          </span>
        </ll-tag>
      </template>

      <!-- 溢出标签气泡 -->
      <a-popover
        v-if="overflowDisplay === 'collapse'"
        trigger="click"
        :overlay-style="{
          ...(overflowTags.length ? {} : { display: 'none !important' }),
          width: moreTagWidth > 0 ? moreTagWidth + 'px' : 'auto',
        }"
        placement="bottomLeft"
        :arrow="false"
        :overlay-class-name="`${bemPopover.b()} ${bemPopover.is(size)}`"
      >
        <!-- 溢出标签 -->
        <ll-tag
          v-show="
            overflowTags.length > 0 || (showSelectAll && !visibleSelectAll)
          "
          ref="ellipsisTagRef"
          :class="[bem.e('tag-item'), bem.is('overflow')]"
          :color="ellipsisSelected ? activeColor : color"
          :bordered="bordered"
          :shape="shape"
          :icon="{ iconName: 'icon-more' }"
          :style="{
            ...tagItemStyle,
            ...(ellipsisSelected ? activeStyle : {}),
          }"
        />

        <template #content>
          <div
            :class="[
              bemPopover.e('container'),
              bem.is(displayMode === 'light' ? 'light' : 'query'),
            ]"
          >
            <!-- 全选标签 -->
            <ll-tag
              v-if="showSelectAll && !visibleSelectAll"
              :class="[bem.e('tag-item'), bem.is('all')]"
              :shape="shape"
              :color="allSelected ? activeColor : color"
              :bordered="bordered"
              :style="{
                ...tagItemStyle,
                ...(allSelected ? activeStyle : {}),
              }"
              @click.stop="handleSelectAll"
            >
              {{ selectAllText }}
            </ll-tag>

            <template v-for="item in overflowTags" :key="item[valueKey]">
              <ll-tag
                :class="[bem.e('tag-item')]"
                :shape="shape"
                :color="
                  selectedValues.includes(item[valueKey]) ? activeColor : color
                "
                :bordered="bordered"
                :icon-direction="iconDirection"
                :icon="{
                  iconName: showIcon ? item[iconKey] || defaultIcon || '' : '',
                  size: iconSize,
                }"
                :style="{
                  ...tagItemStyle,
                  ...(selectedValues.includes(item[valueKey])
                    ? activeStyle
                    : {}),
                }"
                v-bind="
                  displayMode === 'query'
                    ? { onClick: (e: MouseEvent) => handleClick(e, item) }
                    : {}
                "
              >
                <span :class="bem.m('text')">
                  {{ item[labelKey] || item[valueKey] }}
                </span>
              </ll-tag>
            </template>
          </div>
        </template>
      </a-popover>
    </div>
  </div>
</template>

<script setup lang="ts">
import { createNamespace } from '@ll-plus/utils'
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import {
  tagCatalogEmits,
  TagCatalogOption,
  tagCatalogProps,
  TagCatalogValueType,
} from './tag-catalog'

defineOptions({ name: 'LlTagCatalog' })

const props = defineProps(tagCatalogProps)
const emits = defineEmits(tagCatalogEmits)
const bem = createNamespace('tag-catalog')
const bemPopover = createNamespace('tag-catalog-popover')

const labelKey = computed(() => props.fieldMappings?.label || 'label')
const valueKey = computed(() => props.fieldMappings?.value || 'value')
const iconKey = computed(() => props.fieldMappings?.icon || 'icon')

const selectedValues = ref([] as TagCatalogValueType[])

const containerRef = ref()
const contentRef = ref()
const selectAllTagRef = ref()
const ellipsisTagRef = ref()

const visibleTags = ref<TagCatalogOption[]>([])
const overflowTags = ref<TagCatalogOption[]>([])
const visibleSelectAll = ref(false)
const cachedEllipsisWidth = ref(0)

let calculateTimer: number | null = null
let resizeObserver: ResizeObserver | null = null

// 全选选中状态
const allSelected = computed(
  () =>
    selectedValues.value.length &&
    selectedValues.value.length === tags.value.length,
)
const gapSize = computed(() => (props.tagGap || 0) + 'px')
const tagItemStyle = computed(() => ({
  maxWidth: props.tagMaxWidth > 0 ? props.tagMaxWidth + 'px' : 'none',
  cursor: props.displayMode === 'query' ? 'pointer' : 'auto',
  ...props.customStyle,
}))

const ellipsisSelected = computed(() => {
  if (
    props.overflowDisplay !== 'collapse' ||
    props.displayMode !== 'query' ||
    overflowTags.value.length === 0
  ) {
    return false
  }
  return overflowTags.value.every((item) =>
    selectedValues.value.includes(item[valueKey.value]),
  )
})

// 是否显示全选标签
const showSelectAll = computed(
  () =>
    props.showSelectAll &&
    props.selectionMode === 'multiple' &&
    props.displayMode === 'query',
)

// 标签列表
const tags = computed(() => {
  const options = Array.isArray(props.options) ? props.options : []
  const map = new Map()

  // 1. query模式：只去重options，完全忽略value
  if (props.displayMode === 'query') {
    for (const item of options) {
      const k = item?.[valueKey.value]
      if (k != null && !map.has(k)) map.set(k, item)
    }
  }
  // 2. 非query模式
  else {
    const vals = new Set(
      props.value
        ? Array.isArray(props.value)
          ? props.value
          : [props.value]
        : [],
    )
    for (const item of options) {
      const k = item?.[valueKey.value]
      if (vals.has(k) && !map.has(k)) map.set(k, item)
    }
  }

  return [...map.values()]
})

// 选择标签
const handleClick = (event: MouseEvent, item: TagCatalogOption) => {
  if (props.selectionMode === 'multiple') {
    selectedValues.value = selectedValues.value.includes(item[valueKey.value])
      ? selectedValues.value.filter((v) => v !== item[valueKey.value])
      : [...selectedValues.value, item[valueKey.value]]
  } else {
    selectedValues.value = [item[valueKey.value]]
  }
  emits('click', event, item)
  emits('select', item[valueKey.value], item, selectedValues.value)
}

// 全选
const handleSelectAll = () => {
  if (!showSelectAll.value) return
  const isSelected = selectedValues.value.length !== tags.value.length
  if (!isSelected) {
    selectedValues.value = []
  } else {
    selectedValues.value = tags.value.map((item) => item[valueKey.value])
  }
  emits('selectAll', isSelected, selectedValues.value)
}

const calculateOverflow = async () => {
  if (props.overflowDisplay !== 'collapse') {
    visibleSelectAll.value = showSelectAll.value
    visibleTags.value = tags.value
    overflowTags.value = []
    return
  }

  await nextTick()

  const container = containerRef.value
  const content = contentRef.value
  const gap = parseInt(gapSize.value)
  const containerWidth = container.getBoundingClientRect().width
  const ellipsisEl = ellipsisTagRef.value?.$el

  let ellipsisWidth = cachedEllipsisWidth.value
  if (ellipsisEl) {
    const currentEllipsisWidth = ellipsisEl.getBoundingClientRect().width
    if (currentEllipsisWidth > 0) {
      ellipsisWidth = currentEllipsisWidth
      cachedEllipsisWidth.value = currentEllipsisWidth
    }
  }

  if (!ellipsisEl && ellipsisWidth === 0) return

  // 未开启全选的逻辑
  if (!showSelectAll.value) {
    visibleSelectAll.value = false
    visibleTags.value = [...tags.value]
    overflowTags.value = []
    await nextTick()

    const tagEls = Array.from(
      content.querySelectorAll(
        `.${bem.e('tag-item')}:not(.${bem.is('overflow')})`,
      ),
    ) as HTMLElement[]
    if (tagEls.length === 0) return

    const tagWidths = tagEls.map((el) => el.getBoundingClientRect().width)
    const totalTagWidth = tagWidths.reduce((sum, w) => sum + w, 0)
    const totalGapWidth = (tagWidths.length - 1) * gap
    const totalContentWidth = totalTagWidth + totalGapWidth

    if (totalContentWidth <= containerWidth) return

    let currentTotalWidth = totalContentWidth
    let cutIndex = tagWidths.length
    while (cutIndex > 0) {
      cutIndex--
      currentTotalWidth -= tagWidths[cutIndex]
      if (cutIndex > 0) currentTotalWidth -= gap
      if (
        currentTotalWidth + (cutIndex > 0 ? gap : 0) + ellipsisWidth <=
        containerWidth
      )
        break
    }

    visibleTags.value = tags.value.slice(0, cutIndex)
    overflowTags.value = tags.value.slice(cutIndex)
    return
  }

  // 开启全选的逻辑
  else {
    visibleSelectAll.value = true
    // 关键：先全显所有普通标签，让DOM处于“全选+所有标签”的物理状态
    visibleTags.value = [...tags.value]
    overflowTags.value = []
    await nextTick()

    const selectAllEl = selectAllTagRef.value?.$el
    // 如果全选DOM没加载完，直接跳过，等下一次ResizeObserver，不瞎改状态
    if (!selectAllEl) return

    const selectAllWidth = selectAllEl.getBoundingClientRect().width

    // 关键修正1：判断“全选+...”的最小宽度
    // 这里必须判断，否则空间极小的时候全选会乱
    if (selectAllWidth + gap + ellipsisWidth > containerWidth) {
      visibleSelectAll.value = false
      visibleTags.value = []
      overflowTags.value = tags.value
      return
    }

    // 关键修正2：【核心】像未开全选那样，获取当前内容区的真实总宽
    // 此时 contentRef 里面的内容是：[全选] [标签1] [标签2] ... [标签N]
    // scrollWidth 就是这些玩意儿加起来真实的物理宽度
    const totalContentWidth = contentRef.value!.scrollWidth

    // 关键修正3：判断是否完全能放下
    // 总宽度 <= 容器宽度，说明连...都不需要显示，直接返回
    if (totalContentWidth <= containerWidth) {
      return
    }

    // 关键修正4：【对齐未开全选的逻辑】从后往前裁
    // 获取所有普通标签的宽度数组
    const tagEls = Array.from(
      contentRef.value!.querySelectorAll(
        `.${bem.e('tag-item')}:not(.${bem.is('all')}):not(.${bem.is('overflow')})`,
      ),
    ) as HTMLElement[]
    const tagWidths = tagEls.map((el) => el.getBoundingClientRect().width)

    // 初始化当前宽度：这里不直接用 totalContentWidth，
    // 而是用“全选宽度 + 全选后的内容宽度”，这样在循环里只裁普通标签
    let currentTotalWidth = totalContentWidth - selectAllWidth - gap
    let cutIndex = tagWidths.length

    while (cutIndex > 0) {
      cutIndex--
      currentTotalWidth -= tagWidths[cutIndex]
      if (cutIndex > 0) currentTotalWidth -= gap

      // 判断条件：全选宽 + 间隙 + 剩余标签宽 + 间隙 + ...宽
      const neededWidth =
        selectAllWidth +
        gap +
        currentTotalWidth +
        (cutIndex > 0 ? gap : 0) +
        ellipsisWidth

      if (neededWidth <= containerWidth) {
        break
      }
    }

    visibleTags.value = tags.value.slice(0, cutIndex)
    overflowTags.value = tags.value.slice(cutIndex)
  }
}

const debouncedCalculate = () => {
  if (calculateTimer) clearTimeout(calculateTimer)
  calculateTimer = window.setTimeout(calculateOverflow, 50)
}

onMounted(() => {
  resizeObserver = new ResizeObserver(debouncedCalculate)
  if (containerRef.value) resizeObserver.observe(containerRef.value)
})

onUnmounted(() => {
  resizeObserver?.disconnect()
  if (calculateTimer) clearTimeout(calculateTimer)
})

watch(
  [tags, showSelectAll, () => props.overflowDisplay, () => props.tagMaxWidth],
  () => {
    if (props.overflowDisplay === 'wrap') {
      cachedEllipsisWidth.value = 0
    }
    debouncedCalculate()
  },
)

watch(
  () => [props.displayMode, props.selectionMode],
  () => {
    selectedValues.value = []
  },
)
defineExpose({ selectedValues })
</script>
